"use strict";
//
// Copyright © 2020 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModelDb = exports.TxDb = exports.MemDb = void 0;
const platform_1 = require("@hcengineering/platform");
const _1 = require(".");
const clone_1 = require("./clone");
const component_1 = __importDefault(require("./component"));
const query_1 = require("./query");
const tx_1 = require("./tx");
const utils_1 = require("./utils");
function deepFreeze(value) {
    if (typeof value !== 'object' || value === null || Object.isFrozen(value))
        return;
    Object.freeze(value);
    for (const key of Object.keys(value)) {
        deepFreeze(value[key]);
    }
}
/**
 * @public
 */
class MemDb extends tx_1.TxProcessor {
    hierarchy;
    parent;
    objectsByClass = new Map();
    objectById = new Map();
    // Documents of the shared parent this db hides (removed or replaced here).
    shadowed = new Set();
    /** `parent` is a shared system model; own documents win, the rest is read from it. */
    constructor(hierarchy, parent) {
        super();
        this.hierarchy = hierarchy;
        this.parent = parent;
    }
    getObjectsByClass(_class) {
        const result = this.objectsByClass.get(_class);
        if (result === undefined) {
            const result = new Map();
            this.objectsByClass.set(_class, result);
            return result;
        }
        return result;
    }
    /** Own documents of a class plus the parent's, minus anything this db shadows. */
    allOfClass(_class) {
        const own = this.objectsByClass.get(_class);
        if (this.parent === undefined) {
            return own === undefined ? [] : Array.from(own.values());
        }
        if (own === undefined && this.shadowed.size === 0) {
            return this.parent.allOfClass(_class);
        }
        const result = [];
        for (const doc of this.parent.allOfClass(_class)) {
            if (!this.shadowed.has(doc._id) && own?.has(doc._id) !== true) {
                result.push(doc);
            }
        }
        if (own !== undefined) {
            result.push(...own.values());
        }
        return result;
    }
    findDoc(_id) {
        const own = this.objectById.get(_id);
        if (own !== undefined)
            return own;
        if (this.shadowed.has(_id))
            return undefined;
        return this.parent?.findDoc(_id);
    }
    cleanObjectByClass(_class, _id) {
        const result = this.objectsByClass.get(_class);
        if (result !== undefined) {
            result.delete(_id);
        }
    }
    getByIdQuery(query, _class) {
        const result = [];
        if (typeof query._id === 'string') {
            const obj = this.findDoc(query._id);
            if (obj !== undefined && this.hierarchy.isDerived(obj._class, _class))
                result.push(obj);
        }
        else if (query._id?.$in !== undefined) {
            const ids = new Set(query._id.$in);
            for (const id of ids) {
                const obj = this.findDoc(id);
                if (obj !== undefined && this.hierarchy.isDerived(obj._class, _class))
                    result.push(obj);
            }
        }
        return result;
    }
    getObject(_id) {
        const doc = this.findDoc(_id);
        if (doc === undefined) {
            throw new platform_1.PlatformError(new platform_1.Status(platform_1.Severity.ERROR, component_1.default.status.ObjectNotFound, { _id }));
        }
        return doc;
    }
    findObject(_id) {
        const doc = this.findDoc(_id);
        return doc;
    }
    async getLookupValue(_class, doc, lookup, result) {
        for (const key in lookup) {
            if (key === '_id') {
                await this.getReverseLookupValue(doc, lookup, result);
                continue;
            }
            const value = lookup[key];
            const tkey = (0, query_1.checkMixinKey)(key, _class, this.hierarchy);
            const refId = (0, _1.getObjectValue)(tkey, doc);
            if (refId == null)
                continue;
            if (Array.isArray(value)) {
                const [_class, nested] = value;
                const objects = await this.findAll(_class, { _id: refId });
                result[key] = objects[0];
                const nestedResult = {};
                const parent = result[key];
                await this.getLookupValue(_class, parent, nested, nestedResult);
                Object.assign(parent, {
                    $lookup: nestedResult
                });
            }
            else {
                const objects = await this.findAll(value, { _id: refId });
                result[key] = objects[0];
            }
        }
    }
    async getReverseLookupValue(doc, lookup, result) {
        for (const key in lookup._id) {
            const value = lookup._id[key];
            if (Array.isArray(value)) {
                const objects = await this.findAll(value[0], { [value[1]]: doc._id });
                result[key] = objects;
            }
            else {
                const objects = await this.findAll(value, { attachedTo: doc._id });
                result[key] = objects;
            }
        }
    }
    async lookup(_class, docs, lookup) {
        const withLookup = [];
        for (const doc of docs) {
            const result = {};
            await this.getLookupValue(_class, doc, lookup, result);
            withLookup.push(Object.assign({}, doc, { $lookup: result }));
        }
        return withLookup;
    }
    async fillAssociations(docs, associations) {
        const withLookup = [];
        for (const doc of docs) {
            const result = await this.getAssociationValue(doc, associations);
            withLookup.push(Object.assign({}, doc, { $associations: result }));
        }
        return withLookup;
    }
    async getAssociationValue(doc, associations) {
        const result = {};
        for (const association of associations) {
            const _id = association[0];
            const assoc = this.findObject(_id);
            if (assoc === undefined)
                continue;
            const isReverse = association[1] === -1;
            const key = !isReverse ? 'docA' : 'docB';
            const key2 = !isReverse ? 'docB' : 'docA';
            const _class = !isReverse ? assoc.classB : assoc.classA;
            const relations = await this.findAll(component_1.default.class.Relation, { association: _id, [key]: doc._id });
            let objects = await this.findAll(_class, { _id: { $in: relations.map((r) => r[key2]) } });
            if (association[2] !== undefined) {
                objects = (0, utils_1.toFindResult)(await this.fillAssociations(objects, association[2]), objects.length);
            }
            result[`${_id}_${!isReverse ? 'b' : 'a'}`] = objects;
        }
        return result;
    }
    async findAll(_class, query, options) {
        let result;
        const baseClass = this.hierarchy.getBaseClass(_class);
        if (Object.prototype.hasOwnProperty.call(query, '_id') &&
            (typeof query._id === 'string' || query._id?.$in !== undefined || query._id === undefined || query._id === null)) {
            result = this.getByIdQuery(query, baseClass);
        }
        else {
            result = this.allOfClass(baseClass);
        }
        result = (0, query_1.matchQuery)(result, query, _class, this.hierarchy, true);
        if (baseClass !== _class) {
            // We need to filter instances without mixin was set
            result = result.filter((r) => r[_class] !== undefined);
        }
        if (options?.lookup !== undefined) {
            result = await this.lookup(_class, result, options.lookup);
            result = (0, query_1.matchQuery)(result, query, _class, this.hierarchy);
        }
        if (options?.associations !== undefined) {
            result = await this.fillAssociations(result, options.associations);
        }
        if (options?.sort !== undefined)
            (0, query_1.resultSort)(result, options?.sort, _class, this.hierarchy, this);
        const total = result.length;
        result = result.slice(0, options?.limit);
        const tresult = this.hierarchy.clone(result);
        const res = tresult.map((it) => this.hierarchy.updateLookupMixin(_class, it, options));
        return (0, utils_1.toFindResult)(res, total);
    }
    async findOne(_class, query, options) {
        return (await this.findAll(_class, query, { ...options, limit: 1 }))[0];
    }
    /**
     * Only in model find without lookups and sorting.
     * Do not clone results, so be aware modifications are not allowed.
     */
    findAllSync(_class, query, options) {
        let result;
        const baseClass = this.hierarchy.getBaseClass(_class);
        if (Object.prototype.hasOwnProperty.call(query, '_id') &&
            (typeof query._id === 'string' || query._id?.$in !== undefined || query._id === undefined || query._id === null)) {
            result = this.getByIdQuery(query, baseClass);
        }
        else {
            result = this.allOfClass(baseClass);
        }
        result = (0, query_1.matchQuery)(result, query, _class, this.hierarchy, true);
        if (baseClass !== _class) {
            // We need to filter instances without mixin was set
            result = result.filter((r) => r[_class] !== undefined);
        }
        if (options?.sort !== undefined)
            (0, query_1.resultSort)(result, options?.sort, _class, this.hierarchy, this);
        const total = result.length;
        result = result.slice(0, options?.limit);
        return (0, utils_1.toFindResult)(result.map((it) => {
            return baseClass !== _class ? this.hierarchy.as(it, _class) : it;
        }), total);
    }
    addDoc(doc) {
        this.objectById.set(doc._id, doc);
        this.indexByClass(doc);
    }
    /** Deep freeze every document held now: a direct write into a shared one must not leak. */
    freeze() {
        for (const doc of this.objectById.values()) {
            deepFreeze(doc);
        }
    }
    /** Copy-on-write: a document of the shared parent must be deep copied, never mutated in place. */
    ownDoc(_id) {
        const own = this.objectById.get(_id);
        if (own !== undefined && !Object.isFrozen(own)) {
            return own;
        }
        if (own !== undefined && this.parent === undefined) {
            throw new Error(`frozen shared model must not be modified: ${_id}`);
        }
        const doc = own ?? this.findDoc(_id);
        if (doc === undefined) {
            return undefined;
        }
        const copy = (0, clone_1.clone)(doc);
        this.objectById.set(copy._id, copy);
        this.shadowed.add(copy._id);
        for (const _class of this.hierarchy.getAncestors(copy._class)) {
            this.getObjectsByClass(_class).set(copy._id, copy);
        }
        this.hierarchy.replaceDoc(copy);
        return copy;
    }
    indexByClass(doc) {
        this.hierarchy.getAncestors(doc._class).forEach((_class) => {
            const arr = this.getObjectsByClass(_class);
            arr.set(doc._id, doc);
        });
    }
    delDoc(_id) {
        const doc = this.findDoc(_id);
        if (doc === undefined) {
            throw new platform_1.PlatformError(new platform_1.Status(platform_1.Severity.ERROR, component_1.default.status.ObjectNotFound, { _id }));
        }
        this.objectById.delete(_id);
        // Hides the parent's copy too - the shared model itself must stay untouched.
        this.shadowed.add(_id);
        this.hierarchy.getAncestors(doc._class).forEach((_class) => {
            this.cleanObjectByClass(_class, _id);
        });
    }
    updateDoc(_id, doc, update) {
        // TODO: track updates on Contact to adjust memdb accounts?
    }
}
exports.MemDb = MemDb;
/**
 * Hold transactions
 *
 * @public
 */
class TxDb extends MemDb {
    txCreateDoc(tx) {
        throw new Error('Method not implemented.');
    }
    txUpdateDoc(tx) {
        throw new Error('Method not implemented.');
    }
    txRemoveDoc(tx) {
        throw new Error('Method not implemented.');
    }
    txMixin(tx) {
        throw new Error('Method not implemented.');
    }
    async tx(tx) {
        this.addDoc(tx);
        return [];
    }
}
exports.TxDb = TxDb;
/**
 * Hold model objects and classes
 *
 * @public
 */
class ModelDb extends MemDb {
    async txCreateDoc(tx) {
        this.addDoc(tx_1.TxProcessor.createDoc2Doc(tx));
        return {};
    }
    addTxes(ctx, txes, doClone) {
        // Materialize first, index by class after: a doc may appear before its own class is created.
        const created = [];
        for (const tx of txes) {
            // Hierarchy references the created instance instead of deserializing its own copy.
            let ownDoc;
            switch (tx._class) {
                case component_1.default.class.TxCreateDoc: {
                    const doc = tx_1.TxProcessor.createDoc2Doc(tx, doClone);
                    this.objectById.set(doc._id, doc);
                    created.push(doc);
                    ownDoc = doc;
                    break;
                }
                case component_1.default.class.TxUpdateDoc: {
                    const cud = tx;
                    const doc = this.ownDoc(cud.objectId);
                    if (doc !== undefined) {
                        this.updateDoc(cud.objectId, doc, cud);
                        tx_1.TxProcessor.updateDoc2Doc(doc, cud);
                    }
                    else {
                        ctx.warn('no document found, failed to apply model transaction, skipping', {
                            _id: tx._id,
                            _class: tx._class,
                            objectId: cud.objectId
                        });
                    }
                    break;
                }
                case component_1.default.class.TxRemoveDoc:
                    try {
                        this.delDoc(tx.objectId);
                    }
                    catch (err) {
                        ctx.warn('no document found, failed to apply model transaction, skipping', {
                            _id: tx._id,
                            _class: tx._class,
                            objectId: tx.objectId
                        });
                    }
                    break;
                case component_1.default.class.TxMixin: {
                    const mix = tx;
                    const doc = this.ownDoc(mix.objectId);
                    if (doc !== undefined) {
                        this.updateDoc(mix.objectId, doc, mix);
                        tx_1.TxProcessor.updateMixin4Doc(doc, mix);
                    }
                    else {
                        ctx.warn('no document found, failed to apply model transaction, skipping', {
                            _id: tx._id,
                            _class: tx._class,
                            objectId: mix.objectId
                        });
                    }
                    break;
                }
            }
            try {
                this.hierarchy.tx(tx, ownDoc);
            }
            catch (err) {
                ctx.warn('failed to apply model transaction to hierarchy, skipping', {
                    _id: tx._id,
                    _class: tx._class,
                    err
                });
            }
        }
        for (const doc of created) {
            // Skip what a later tx of the same batch removed or replaced.
            if (this.objectById.get(doc._id) === doc)
                this.indexByClass(doc);
        }
    }
    async txUpdateDoc(tx) {
        try {
            const doc = this.ownDoc(tx.objectId);
            this.updateDoc(tx.objectId, doc, tx);
            tx_1.TxProcessor.updateDoc2Doc(doc, tx);
            return tx.retrieve === true ? { object: doc } : {};
        }
        catch (err) { }
        return {};
    }
    async txRemoveDoc(tx) {
        try {
            this.delDoc(tx.objectId);
        }
        catch (err) { }
        return {};
    }
    // TODO: process ancessor mixins
    async txMixin(tx) {
        const doc = this.ownDoc(tx.objectId);
        if (doc === undefined) {
            throw new platform_1.PlatformError(new platform_1.Status(platform_1.Severity.ERROR, component_1.default.status.ObjectNotFound, { _id: tx.objectId }));
        }
        this.updateDoc(tx.objectId, doc, tx);
        tx_1.TxProcessor.updateMixin4Doc(doc, tx);
        return {};
    }
}
exports.ModelDb = ModelDb;
//# sourceMappingURL=memdb.js.map